/**
 * Player feedback: the decisions the dialog makes, kept out of it so they can be
 * tested (there is no DOM test runner in this app). The report pipeline's
 * pattern (`src/report/report.ts`), for a different conversation: this one is
 * about the game, not about another player.
 *
 * The server owns every rule that matters - the categories, the 512-character
 * cap, the hourly limit, the off switch - and refuses anything else. These copies
 * exist so the dialog never offers something the server would refuse.
 */
import type { DeviceInfo } from "./device";

/**
 * Wire ids in the order the dialog lists them. `online` and `voice` are their
 * own because they are debugged somewhere else entirely (the server, Cloudflare)
 * from a rules bug; `praise` because what people like is what not to break.
 */
export const FEEDBACK_CATEGORIES = [
  { id: "bug", label: "Bug" },
  { id: "online", label: "Online or connection problem" },
  { id: "voice", label: "Voice chat" },
  { id: "friction", label: "Something is awkward" },
  { id: "change", label: "Change suggestion" },
  { id: "feature", label: "Feature request" },
  { id: "praise", label: "Something I like" },
  { id: "other", label: "Other" },
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]["id"];

export function feedbackCategoryLabel(id: FeedbackCategory): string {
  return FEEDBACK_CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

/**
 * The server's cap, in CHARACTERS (Go runes). The textarea's `maxLength` counts
 * UTF-16 units, which is never fewer than code points, so text the box accepts
 * can never be over the server's limit - an emoji just uses up two of the box's.
 */
export const FEEDBACK_TEXT_MAX = 512;

/** Characters as the server counts them: code points, not UTF-16 units. */
export function textLength(text: string): number {
  return Array.from(text).length;
}

export function textCounter(text: string): string {
  return `${textLength(text)} / ${FEEDBACK_TEXT_MAX}`;
}

/** Whether Preview may be pressed: a category, and something to say that the server would take. */
export function canPreview(category: FeedbackCategory | null, text: string): boolean {
  const n = textLength(text.trim());
  return category !== null && n > 0 && n <= FEEDBACK_TEXT_MAX;
}

/** What `POST /feedback` takes. The sender is the verified token; nothing here names them. */
export interface FeedbackRequest {
  /**
   * Made once, when the feedback is previewed, and kept through every retry:
   * the server keys the document on it, so a resend is never a duplicate.
   */
  clientId: string;
  category: FeedbackCategory;
  text: string;
  clientVersion: string;
  appEnv: string;
  /** The route PATTERN (`/online/play/:gameId`), so feedback groups by screen. */
  screen: string;
  /** The online game on screen, "" anywhere else. */
  gameId: string;
  /** The device's own clock, ISO 8601 with its offset. The server stamps its own time too. */
  clientTime: string;
  timeZone: string;
  client: DeviceInfo;
}

/** `2026-10-10T09:41:07-06:00`: local time with the offset, which `toISOString` (UTC) loses. */
export function localIsoTime(d: Date): string {
  const pad = (n: number) => String(Math.trunc(Math.abs(n))).padStart(2, "0");
  // getTimezoneOffset counts minutes WEST of UTC, so it is negated.
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(off / 60)}:${pad(off % 60)}`
  );
}

/** The game id in an online route, "" for anything else. */
export function gameIdFromPath(path: string): string {
  const m = /^\/online\/(?:lobby|play|leaderboard)\/([^/?#]+)/.exec(path);
  return m?.[1] ?? "";
}

export interface PreviewRow {
  label: string;
  value: string;
}

/**
 * Everything that will be sent, as the preview lists it - the WHOLE request,
 * plus the two things the server adds from the connection (who is sending, and
 * the browser's own identification string), so nothing leaves that was not on
 * this list. The order is what a player cares about first.
 */
export function previewRows(req: FeedbackRequest, sender: { playerId: string; signedIn: boolean }, userAgent: string): PreviewRow[] {
  const c = req.client;
  const join = (...parts: (string | number)[]) => parts.filter((p) => p !== "" && p !== 0).join(" ");
  const rows: PreviewRow[] = [
    { label: "Category", value: feedbackCategoryLabel(req.category) },
    { label: "Message", value: req.text },
    { label: "Game version", value: req.appEnv === "prod" ? req.clientVersion : `${req.clientVersion} (${req.appEnv})` },
    { label: "Date", value: `${req.clientTime} (${req.timeZone})` },
    { label: "Browser", value: join(c.browser, c.browserVersion) || "Unknown" },
    { label: "Operating system", value: join(c.os, c.osVersion) || "Unknown" },
    { label: "Device", value: join(c.deviceType, c.deviceModel ? `· ${c.deviceModel}` : "") },
    {
      label: "Screen",
      value: `${c.screenWidth}×${c.screenHeight} at ${c.pixelRatio}x, window ${c.viewportWidth}×${c.viewportHeight}, ${c.orientation}`,
    },
    {
      label: "Hardware",
      value: [
        c.cpuCores ? `${c.cpuCores} cores` : "",
        c.memoryGb ? `${c.memoryGb} GB memory` : "",
        c.touchPoints ? `touch (${c.touchPoints} points)` : "no touch",
      ]
        .filter(Boolean)
        .join(", "),
    },
    { label: "Installed app", value: c.standalone ? "Yes" : "No, in a browser tab" },
    { label: "Language", value: c.language || "Unknown" },
    { label: "Connection", value: c.connection || "Not reported" },
    { label: "Where you were", value: req.screen },
  ];
  if (req.gameId) rows.push({ label: "Online game", value: req.gameId });
  rows.push(
    {
      label: "Player id",
      value: sender.playerId
        ? `${sender.playerId}${sender.signedIn ? " (signed in with Google)" : " (guest)"}`
        : "A guest id, created when you send",
    },
    { label: "Browser identification", value: userAgent },
    { label: "Feedback id", value: req.clientId },
  );
  return rows;
}

/**
 * Where a Send left the dialog. `sent` and `queued` are both done - a queued
 * feedback is saved on this device and goes on its own later - so both end on
 * Done. Only `error` (it could not even be saved) offers Try again; `rejected`
 * is the server refusing the request itself, which only an Edit can change.
 */
export type FeedbackOutcome =
  | { kind: "sent" }
  | { kind: "queued"; message: string }
  | { kind: "rejected"; message: string }
  | { kind: "error"; message: string };

/** Words for an outbox result (outbox.ts `SubmitResult`), keyed off the server's refusal codes. */
export function feedbackOutcome(result: { kind: "sent" } | { kind: "queued" | "rejected" | "unsaved"; error: string }): FeedbackOutcome {
  if (result.kind === "sent") return { kind: "sent" };
  const code = result.error;
  if (result.kind === "queued") {
    const why =
      code === "too-much-feedback"
        ? "You've sent a lot of feedback in the last hour"
        : code === "feedback-off"
          ? "We're not taking feedback just now"
          : "We couldn't reach the server";
    return { kind: "queued", message: `${why}, so it's saved on this device and will be sent automatically later.` };
  }
  if (result.kind === "rejected") {
    if (code === "feedback-too-long") return { kind: "rejected", message: `Keep it to ${FEEDBACK_TEXT_MAX} characters.` };
    if (code === "feedback-empty") return { kind: "rejected", message: "Write something first." };
    return { kind: "rejected", message: code || "The server couldn't accept that." };
  }
  // Not saved (storage blocked) AND not sent: the words are only on screen now.
  return { kind: "error", message: "Couldn't send it, and this browser won't let us save it to try later. Try again in a moment." };
}
